import { notifyError, notifyInfo, resolveUrl } from "@serenity-is/corelib";
import { HubConnectionBuilder } from "@microsoft/signalr";

const COUNTER_KEYS_FOR_BELL = (() => {
    const bell = document.getElementById("notifBell");
    const include = bell?.getAttribute("data-bell-include") ?? "";
    return include.split(",").map(s => s.trim()).filter(Boolean); // e.g., ["memo_pending","credit_pending","payment_pending"]
})();

// --- SignalR (keep) ---
const connection = new HubConnectionBuilder()
    .withUrl("/notificationhub")
    .withAutomaticReconnect()
    .build();

connection.on("notifyMessage", (m: string) => { notifyInfo(m); updateCounters(); });
connection.on("notifyMessageRed", (m: string) => notifyError(m));
connection.on("RefreshNav", () => updateCounters());

connection.start().catch(e => console.error(e));

// --- Counters updater (new, lightweight) ---
async function updateCounters() {
    try {
        const url = resolveUrl("~/Dashboard/GetCounters");
        const res = await fetch(url, { headers: { "Accept": "application/json" }, cache: "no-store" });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const dict: Record<string, number | string> = await res.json();

        // 1) Per-menu badges (leaf links rendered by _sidebar)
        document.querySelectorAll<HTMLAnchorElement>('.s-sidebar-link[data-counter-key]').forEach(a => {
            const key = a.getAttribute('data-counter-key') ?? '';
            const badge = a.querySelector<HTMLElement>('[data-badge]');
            const raw = dict[key];
            const val = typeof raw === 'string' ? parseInt(raw, 10) : (raw as number | undefined);

            if (badge) {
                if (val && val > 0) {
                    badge.textContent = String(val);
                    badge.removeAttribute('hidden');
                } else {
                    badge.setAttribute('hidden', 'hidden');
                    badge.textContent = '0';
                }
            }
        });

        // 2) Bell badge + list
        const bell = document.getElementById('notifBell');
        if (bell) {
            const bellBadge = bell.querySelector<HTMLElement>('[data-badge="bell"]');
            const bellHeader = bell.querySelector<HTMLElement>('[data-bell-header]');
            const bellList = bell.querySelector<HTMLElement>('[data-bell-list]');

            // collect included counters in the order declared in data-bell-include
            const items: Array<{ key: string; value: number; label: string; url?: string }> = [];
            for (const k of COUNTER_KEYS_FOR_BELL) {
                const raw = dict[k];
                const val = typeof raw === 'string' ? parseInt(raw, 10) : (raw as number | undefined) || 0;
                if (val > 0) {
                    // try to find a matching sidebar link to derive its label and drill URL
                    const link = document.querySelector<HTMLAnchorElement>(`.s-sidebar-link[data-counter-key="${k}"]`);
                    const label = (link?.querySelector('.s-sidebar-link-text')?.textContent ?? k)
                        .toString().trim();
                    const drill = link?.getAttribute('data-drill-url') ?? '#';
                    items.push({ key: k, value: val, label, url: drill });
                }
            }

            const total = items.reduce((s, x) => s + x.value, 0);

            if (bellBadge) {
                if (total > 0) { bellBadge.textContent = String(total); bellBadge.removeAttribute('hidden'); }
                else { bellBadge.textContent = '0'; bellBadge.setAttribute('hidden', 'hidden'); }
            }

            if (bellHeader) bellHeader.textContent = `${total} NOTIFICATIONS`;
            if (bellList) {
                bellList.innerHTML = '';
                for (const it of items) {
                    const li = document.createElement('li');
                    li.innerHTML = `
            <a class="dropdown-item d-flex align-items-center gap-2" href="${it.url ?? '#'}">
              <i class="fa fa-exclamation-circle text-danger"></i>
              <span class="flex-grow-1">${it.label}</span>
              <span class="badge rounded-pill bg-warning text-dark">${it.value}</span>
            </a>`;
                    bellList.appendChild(li);
                }
            }
        }
    } catch (err) {
        console.error('updateCounters failed:', err);
    }
}

// initial fill + small poll as fallback (optional)
document.addEventListener('DOMContentLoaded', () => {
    updateCounters();
    // setInterval(updateCounters, 60_000); // uncomment if you want periodic refresh in addition to SignalR
});
